import {
  CartItem,
  CartItemCategory,
  CartItemOptionItems,
  CartItemProduct,
  getPropInSelectedLanguage,
  OptionConfiguration,
  PricingError,
  ProductOptionItem,
  ProductOptionRangeType,
  SUPPORTED_LANGUAGES,
  HiddenProduct,
} from '@pedix-workspace/utils';
import { ApiV1_Category, ApiV1_Product } from '@pedix-workspace/api-v1';
import { cloneDeep, isEqual } from 'lodash-es';

export type CartItemType = 'regular' | 'single-presentation' | 'multi-presentation';

export type GetCartItemDetailsOptions = {
  skipPresentations?: boolean;
  selectedLanguage?: SUPPORTED_LANGUAGES;
  displaySku?: boolean;
};

export type CartItemDetailsItems = {
  optionName?: string;
  items: {
    name: string;
    sku?: string;
    quantity: number;
  }[];
};

export function getCartItemDetailsText(
  cartItem: CartItem,
  { skipPresentations, selectedLanguage, displaySku }: GetCartItemDetailsOptions = {},
): string {
  const cartItemDetailsItems = getCartItemDetailsItemsList(cartItem, {
    skipPresentations,
    selectedLanguage,
    displaySku,
  });
  const details = cartItemDetailsItems.map(getCartItemDetailsItemToText);

  return details.join('\n');
}

export function getCartItemDetailsItemsList(
  cartItem: CartItem,
  options: {
    skipPresentations?: boolean;
    selectedLanguage?: SUPPORTED_LANGUAGES;
    displaySku?: boolean;
  },
): CartItemDetailsItems[] {
  const cartItemDetailsItems: CartItemDetailsItems[] = [];

  if (cartItem.selectedPresentation && !options.skipPresentations) {
    const detailItems = getCartItemDetailsPresentationItems(
      cartItem.selectedPresentation.items,
      options.selectedLanguage,
      options.displaySku,
    );

    cartItemDetailsItems.push(detailItems);
  }
  if (cartItem.selectedOptions) {
    cartItem.selectedOptions.forEach(({ option, items }) => {
      const optionItemMultiplier = getOptionItemMultiplier(cartItem);
      const detailItems = getCartItemDetailsOptionItems(
        items,
        option,
        optionItemMultiplier,
        options.selectedLanguage,
        options.displaySku,
      );

      cartItemDetailsItems.push(detailItems);
    });
  }

  return cartItemDetailsItems;
}

export function getCartItemDetailsPresentationItems(
  items: ProductOptionItem[],
  selectedLanguage?: SUPPORTED_LANGUAGES,
  displaySku?: boolean,
): CartItemDetailsItems {
  return {
    optionName: undefined,
    items: items.map(item => {
      return {
        name: getPropInSelectedLanguage(item, 'name', selectedLanguage, true),
        sku: displaySku && item.sku ? item.sku : undefined,
        quantity: item.quantity,
      };
    }),
  };
}
export function getCartItemDetailsOptionItems(
  items: ProductOptionItem[],
  option: OptionConfiguration,
  optionItemMultiplier: number,
  selectedLanguage?: SUPPORTED_LANGUAGES,
  displaySku?: boolean,
): CartItemDetailsItems {
  return {
    optionName: getPropInSelectedLanguage(option, 'name', selectedLanguage, true),
    items: items.map(item => {
      return {
        name: getPropInSelectedLanguage(item, 'name', selectedLanguage, true),
        sku: displaySku && item.sku ? item.sku : undefined,
        quantity: getOptionItemQuantity(option, item, optionItemMultiplier),
      };
    }),
  };
}

export function getCartItemDetailsItemToText(cartItemDetailsItem: CartItemDetailsItems): string {
  const itemTexts = cartItemDetailsItem.items.map(item => {
    const itemName = item.sku ? `${item.sku} | ${item.name}` : item.name;

    return (
      itemName +
      (cartItemDetailsItem.items.length > 1 || item.quantity > 1 ? ` (x${item.quantity})` : '')
    );
  });

  if (cartItemDetailsItem.optionName) {
    return `- ${cartItemDetailsItem.optionName}: ${itemTexts.join(', ')}`;
  }
  return itemTexts.join(', ');
}

export function consolidateCartItems(cartItems: CartItem[], displaySku: boolean) {
  const consolidatedCartItems = cartItems.reduce<{ [key: string]: CartItem }>(
    (consolidatedItems, cartItem) => {
      const consolidatedKey = getCartItemConsolidationKey(cartItem, displaySku);

      if (consolidatedItems[consolidatedKey]) {
        // If product has presentation with quantities, then we sum up their quantities individually
        if (consolidatedItems[consolidatedKey].product.presentations?.displayItemQuantities) {
          consolidatedItems[consolidatedKey] = mergeCartItemPresentationItems(
            consolidatedItems[consolidatedKey],
            cartItem,
          );
        } else {
          consolidatedItems[consolidatedKey].qty += cartItem.qty;
        }
      } else {
        consolidatedItems[consolidatedKey] = { ...cartItem };
      }
      return consolidatedItems;
    },
    {},
  );

  return Object.values(consolidatedCartItems);
}

export function getCartItemConsolidationKey(cartItem: CartItem, displaySku: boolean) {
  const skipPresentations = cartItem.selectedPresentation?.option.displayItemQuantities
    ? true
    : false;

  return JSON.stringify([
    cartItem.product.id,
    cartItem.product.categoryId,
    getCartItemDetailsText(cartItem, { skipPresentations, displaySku }),
    cartItem.observations || '',
    cartItem.selectedPresentation?.option.id || '',
  ]);
}

export function mergeCartItemPresentationItems(cartItemA: CartItem, cartItemB: CartItem) {
  const cartItemCopy = { ...cartItemA };

  cartItemCopy.selectedPresentation.items = cartItemCopy.product?.presentations.items.reduce(
    (items, currentItem) => {
      const itemOnA = cartItemA.selectedPresentation.items.find(item => item.id === currentItem.id);
      const itemOnB = cartItemB.selectedPresentation.items.find(item => item.id === currentItem.id);

      const quantity = (itemOnA?.quantity || 0) + (itemOnB?.quantity || 0);

      if (quantity > 0) {
        items.push({
          ...currentItem,
          quantity,
        });
      }
      return items;
    },
    [],
  );

  return cartItemCopy;
}

export function calculateCartItemsTotal(
  cartItems: CartItem[],
  options: { usePriceDiscount: boolean },
) {
  return cartItems.reduce(
    (total, cartItem) => (total += getCartItemSubtotal(cartItem, options)),
    0,
  );
}

export function getCartItemSubtotal(
  cartItem: {
    product: CartItemProduct;
    qty: CartItem['qty'];
    selectedPresentation?: CartItem['selectedPresentation'];
    selectedOptions?: CartItem['selectedOptions'];
  },
  options: { usePriceDiscount: boolean },
) {
  let price = 0;

  if (getItemType(cartItem) === 'multi-presentation') {
    price += getMultiPresentationTotal(cartItem, options);
  } else {
    price += getProductOrSinglePresentationTotal(cartItem, options);
  }

  if (cartItem.selectedOptions) {
    price += getOptionsTotal(cartItem, options);
  }

  return price;
}

export function getItemType({
  selectedPresentation,
}: Pick<CartItem, 'selectedPresentation'>): CartItemType {
  if (!selectedPresentation || selectedPresentation?.items.length === 0) {
    return 'regular';
  }
  if (selectedPresentation.items.length > 1) {
    return 'multi-presentation';
  }
  return 'single-presentation';
}

export function getProductOrSinglePresentationQuantity(
  cartItem: Pick<CartItem, 'qty' | 'selectedPresentation'>,
) {
  switch (getItemType(cartItem)) {
    case 'regular':
      return cartItem.qty || 1;
    case 'single-presentation':
      // FIXME: we need to do this, because when we display single-presentation selector, the product-quantity selector updates the cartItem.qty prop, but the item.quantity is set to 1
      if (
        cartItem.selectedPresentation.option.rangeType === ProductOptionRangeType.FIXED &&
        cartItem.selectedPresentation.option.min === 1
      ) {
        return cartItem.qty || 1;
      }
      return cartItem.selectedPresentation.items[0].quantity;
    case 'multi-presentation':
      return 1;
  }
}
export function getOptionItemMultiplier(
  cartItem: Pick<CartItem, 'qty' | 'selectedPresentation'>,
): number {
  if (getItemType(cartItem) === 'multi-presentation') {
    return getPresentationsCount(cartItem.selectedPresentation);
  }
  return getProductOrSinglePresentationQuantity(cartItem);
}

export function countCartUnits(
  cartItems: Pick<CartItem, 'qty' | 'selectedPresentation'>[],
): number {
  return cartItems.reduce((total, cartItem) => total + getOptionItemMultiplier(cartItem), 0);
}

export function getItemPrice(
  item: CartItemProduct | ProductOptionItem,
  options: { usePriceDiscount: boolean },
) {
  if (options.usePriceDiscount && item.priceDiscount) {
    return item.priceDiscount;
  }
  return item.price || 0;
}

export function getProductOrSinglePresentationUnitPrice(
  cartItem: Pick<CartItem, 'product' | 'selectedPresentation'>,
  options: { usePriceDiscount: boolean },
) {
  switch (getItemType(cartItem)) {
    case 'regular':
      return getItemPrice(cartItem.product, options);
    case 'single-presentation':
      return getItemPrice(cartItem.selectedPresentation.items[0], options);
    case 'multi-presentation':
      return 0;
  }
}

export function getProductOrSinglePresentationTotal(
  cartItem: Pick<CartItem, 'product' | 'selectedPresentation' | 'selectedOptions' | 'qty'>,
  options: { usePriceDiscount: boolean },
) {
  switch (getItemType(cartItem)) {
    case 'regular':
      return (
        getItemPrice(cartItem.product, options) * getProductOrSinglePresentationQuantity(cartItem)
      );
    case 'single-presentation':
      return (
        getItemPrice(cartItem.selectedPresentation.items[0], options) *
        getProductOrSinglePresentationQuantity(cartItem)
      );
    case 'multi-presentation':
      return 0;
  }
}

export function getMultiPresentationTotal(
  cartItem: Pick<CartItem, 'selectedPresentation'>,
  options: { usePriceDiscount: boolean },
) {
  if (getItemType(cartItem) !== 'multi-presentation') {
    return 0;
  }

  return cartItem.selectedPresentation.items.reduce((sum, item) => {
    const itemPrice = getItemPrice(item, options) * (item.quantity || 1);

    return sum + itemPrice;
  }, 0);
}

export function getOptionsTotal(
  cartItem: Pick<CartItem, 'selectedOptions' | 'qty' | 'selectedPresentation'>,
  options: { usePriceDiscount: boolean },
) {
  if (!cartItem.selectedOptions) {
    return 0;
  }

  let price = 0;

  cartItem.selectedOptions.forEach(selectedOption => {
    selectedOption?.items?.forEach(item => {
      const optionItemMultiplier = getOptionItemMultiplier(cartItem);

      price += getOptionItemTotal(selectedOption.option, item, optionItemMultiplier, options);
    });
  });

  return price;
}

export function getOptionItemQuantity(
  option: OptionConfiguration,
  item: ProductOptionItem,
  optionItemMultiplier: number,
) {
  const itemMultiplier = option.type === 'customization' ? optionItemMultiplier : 1;

  return (item.quantity || 1) * itemMultiplier;
}

export function getOptionItemUnitPrice(
  item: ProductOptionItem,
  options: { usePriceDiscount: boolean },
) {
  return getItemPrice(item, options);
}

export function getOptionItemTotal(
  option: OptionConfiguration,
  item: ProductOptionItem,
  optionItemMultiplier: number,
  options: { usePriceDiscount: boolean },
) {
  return (
    getOptionItemQuantity(option, item, optionItemMultiplier) *
    getOptionItemUnitPrice(item, options)
  );
}

export function getPresentationsCount(selectedPresentation: CartItem['selectedPresentation']) {
  return selectedPresentation.items.reduce(
    (sum, presentationItem) => sum + presentationItem.quantity,
    0,
  );
}

export function getCartProductIds(cartItems: CartItem[]): string[] {
  const productIds: Set<string> = new Set();

  cartItems.forEach(cartItem => {
    productIds.add(cartItem.product.id);

    if (cartItem.selectedOptions) {
      cartItem.selectedOptions.forEach(selectedOption => {
        selectedOption.items.forEach(item => {
          if (item.isOptionItem) {
            productIds.add(item.id);
          }
        });
      });
    }
  });

  return Array.from(productIds);
}

export function toCartItemCategory(
  category: Omit<CartItemCategory, 'establishmentId'>,
  establishmentId: string,
): CartItemCategory {
  return {
    id: category.id,
    name: category.name,
    image: category.image,
    active: category.active,
    availability: category.availability,
    establishmentId,
    clonedFrom: category.clonedFrom,
  };
}

export function toCartItemProduct(
  product: Omit<CartItemProduct, 'categoryId' | 'establishmentId'>,
  categoryId: string,
  establishmentId: string,
): CartItemProduct {
  return {
    id: product.id,
    categoryId,
    establishmentId,
    description: product.description,
    name: product.name,
    sku: product.sku,
    price: product.price,
    priceDiscount: product.priceDiscount,
    aliquot: product.aliquot,
    groupName: product.groupName,
    images: product.images,
    linkedProductTagIds: product.linkedProductTagIds,
    presentations: product.presentations,
    weight: product.weight,
    height: product.height,
    length: product.length,
    width: product.width,
    clonedFrom: product.clonedFrom,
    allowedShippingOptions: product.allowedShippingOptions,
    allowedPaymentMethods: product.allowedPaymentMethods,
  };
}

export function createOrUpdateCartItem(params: {
  category: ApiV1_Category;
  product: ApiV1_Product;
  establishmentId: string;
  cartItemId: string;
  selectedPresentationOption: CartItemOptionItems;
  productQuantity: number;
  productObservations: string;
  selectedOptions: CartItemOptionItems[];
}): CartItem {
  const cartItemCategory = toCartItemCategory(params.category, params.establishmentId);
  const cartItemProduct = toCartItemProduct(params.product, params.category.id, params.establishmentId);
  return cloneDeep({
    id: params.cartItemId,
    category: cartItemCategory,
    product: cartItemProduct,
    selectedPresentation: params.selectedPresentationOption,
    qty: params.productQuantity,
    observations: params.productObservations?.trim(),
    selectedOptions: params.selectedOptions.filter(cartItemOption => Boolean(cartItemOption)),
  });
}

export function updateCartItemPrices(
  cartItems: CartItem[],
  pricingErrors: PricingError[],
): CartItem[] {
  const cartItemsCopy = cloneDeep(cartItems);

  pricingErrors.forEach(pricingError => {
    switch (pricingError.type) {
      case 'product':
        cartItemsCopy.forEach(cartItem => {
          if (cartItem.product.id === pricingError.id) {
            cartItem.product.price = pricingError.newPrice;
            cartItem.product.priceDiscount = pricingError.newPriceDiscount;
          }
        });
        break;
      case 'presentation':
        cartItemsCopy.forEach(cartItem => {
          cartItem.selectedPresentation?.items.forEach(presentationItem => {
            if (presentationItem.id === pricingError.id) {
              presentationItem.price = pricingError.newPrice;
              presentationItem.priceDiscount = pricingError.newPriceDiscount;
            }
          });
        });
        break;
      case 'option-item':
        cartItemsCopy.forEach(cartItem => {
          cartItem.selectedOptions?.forEach(selectedOption => {
            selectedOption.items?.forEach(optionItem => {
              if (optionItem.id === pricingError.id) {
                optionItem.price = pricingError.newPrice;
                optionItem.priceDiscount = pricingError.newPriceDiscount;
              }
            });
          });
        });
        break;
    }
  });

  return cartItemsCopy;
}

export function syncCartItemPurchaseRestrictions(
  cartItems: CartItem[],
  products: Pick<ApiV1_Product, 'id' | 'allowedShippingOptions' | 'allowedPaymentMethods'>[],
): CartItem[] {
  if (cartItems.length === 0) {
    return cartItems;
  }
  const productsById = new Map(products.map(product => [product.id, product]));
  let changed = false;
  const syncedCartItems = cartItems.map(cartItem => {
    const product = productsById.get(cartItem.product.id);

    if (
      !product ||
      (isEqual(product.allowedShippingOptions, cartItem.product.allowedShippingOptions) &&
        isEqual(product.allowedPaymentMethods, cartItem.product.allowedPaymentMethods))
    ) {
      return cartItem;
    }
    changed = true;

    return {
      ...cartItem,
      product: {
        ...cartItem.product,
        allowedShippingOptions: product.allowedShippingOptions,
        allowedPaymentMethods: product.allowedPaymentMethods,
      },
    };
  });

  return changed ? syncedCartItems : cartItems;
}

export function markCartItemProductsAsHidden(
  cartItems: CartItem[],
  hiddenProducts: HiddenProduct[],
): CartItem[] {
  const cartItemsCopy = cloneDeep(cartItems);

  cartItemsCopy.forEach(cartItem => {
    const isHidden = hiddenProducts.some(hiddenProduct => {
      if (hiddenProduct.id === cartItem.product.id) {
        return (
          !hiddenProduct.presentationId ||
          !!cartItem.selectedPresentation?.items.some(
            item => item.id === hiddenProduct.presentationId,
          )
        );
      }
      return !!cartItem.selectedOptions?.some(selectedOption =>
        selectedOption.items.some(item => item.isOptionItem && item.id === hiddenProduct.id),
      );
    });

    if (isHidden) {
      cartItem.product.hidden = true;
    }
  });

  return cartItemsCopy;
}
